// Board-agnostic Gerber/drill emission (Task 2). Pure, no Angular / DI deps.
//
// Two layers live here:
//
// 1. Low-level pure functions (`gc`, `x2Attributes`, `renderCopperLayer`,
//    `renderSolderMask`, `renderEdgeCuts`, `renderDrillFile`,
//    `renderMountingDrill`) — these are the STATOR's existing string-building
//    bodies, moved verbatim (same math, same line order, same D-code
//    sequencing) from `PcbStatorComponent`. `PcbStatorComponent`'s public
//    emission methods now delegate to these one-line; the moments-spec X2
//    tests and the `pcb-stator-gerber.spec.ts` golden hashes pin their
//    output byte-for-byte against the pre-refactor component.
//
// 2. The generic `BoardDescriptor` / `emitBoard` model the brief's interface
//    freezes for Tasks 6+ (new board types: rotor recess, encoder ring,
//    target ring). `GerberDraw.points` is a flat vertex list with no
//    provision for arc curvature (no I/J center or sweep direction), so
//    `emitBoard` always renders straight polylines/flashes/regions — it is
//    NOT used for the stator's actual fab output (which is arc-heavy and
//    stays on the byte-stable functions above). `statorBoardDescriptor` on
//    the component populates this model best-effort for descriptive/preview
//    consumers (e.g. a future stack-preview UI or DRC pass): coil/bus arcs
//    are represented as straight start→end chords, and `outline` is the
//    board's OUTER profile only — the interface has no way to express the
//    stator's inner-bore second boundary (an annulus is two disjoint closed
//    loops, not one). Future board work should account for this if it needs
//    exact annular outlines through this interface.

import { GerberData, BusRouting, StatorParams, CopperLayer } from './pcb-stator-geometry';
import { MountingHole } from './pcb-stator-mounting';
import { EAR_HOLE_DRILL_MM } from './pcb-stator-stacking-tabs';

// ── Coordinate + X2 helpers (shared by both layers above) ──────────────────

/** Gerber fixed-point coordinate (6 decimal digits, %FSLAX36Y36%). */
export function gc(mm: number): string {
  return Math.round(mm * 1e6).toString();
}

/** X2 file attributes — how CAM tools (JLCPCB) learn each file's layer role
 *  regardless of filename. Placed before the FS declaration. */
export function x2Attributes(fileFunction: string, polarity: 'Positive' | 'Negative' = 'Positive'): string[] {
  return [
    '%TF.GenerationSoftware,steve-corry,pcb-stator,1.0*%',
    `%TF.FileFunction,${fileFunction}*%`,
    `%TF.FilePolarity,${polarity}*%`,
  ];
}

/** X2 FileFunction for copper layer i: Copper,L<n>,Top|Inr|Bot (KiCad convention). */
export function copperFileFunction(i: number, numLayers: number): string {
  const pos = i === 0 ? 'Top' : i === numLayers - 1 ? 'Bot' : 'Inr';
  return `Copper,L${i + 1},${pos}`;
}

function gerberHeader(
  layerName: string, layerIndex: number, params: StatorParams, viaPadMm: number, termPadMm: number,
): string[] {
  return [
    ...x2Attributes(copperFileFunction(layerIndex, params.numLayers)),
    // ASCII only: JLCPCB's analyzer failed to classify layers when this
    // header carried a multi-byte em-dash (no copper, wrong board dims).
    `G04 PCB Stator - ${layerName} (concentrated spiral) *`,
    '%FSLAX36Y36*%',
    '%MOMM*%',
    '%LPD*%',
    `%ADD10C,${params.traceWidth.toFixed(4)}*%`,
    `%ADD11C,${viaPadMm.toFixed(4)}*%`,
    `%ADD12C,${termPadMm.toFixed(4)}*%`,
    'G75*',
    'G01*',
  ];
}

// ── Stator emission (byte-identical to the pre-refactor component) ─────────

/** Copper-layer Gerber content — bus segments/arcs, spiral legs/arcs, and
 *  chaining/bridge/exit-via + terminal-class selective pads, in the original
 *  fixed pass order. */
export function renderCopperLayer(
  layerIndex: number,
  layerName: CopperLayer,
  data: GerberData,
  bus: BusRouting,
  params: StatorParams,
  viaPadMm: number,
  termPadMm: number,
): string {
  const lines = gerberHeader(layerName.replace('.', '_'), layerIndex, params, viaPadMm, termPadMm);

  // 1. Bus segments on this layer (always F.Cu pad-to-arc; bridge-via tails handled implicitly via arcs).
  lines.push('D10*');
  for (const s of bus.segments.filter(s => s.layer === layerName)) {
    lines.push(`X${gc(s.startX)}Y${gc(-s.startY)}D02*`);
    lines.push(`X${gc(s.endX)}Y${gc(-s.endY)}D01*`);
  }

  // 2. Bus arcs on this layer.
  for (const arc of bus.arcs.filter(a => a.layer === layerName)) {
    const I = gc(-arc.startX);
    const J = gc(arc.startY);
    const gCode = arc.sweepCW ? 'G02' : 'G03';
    lines.push('D10*');
    lines.push(`X${gc(arc.startX)}Y${gc(-arc.startY)}D02*`);
    lines.push(`${gCode}*`);
    lines.push(`X${gc(arc.endX)}Y${gc(-arc.endY)}I${I}J${J}D01*`);
    lines.push('G01*');
  }

  // 3. Spiral legs (and tails, arcIndex=-2) on this layer.
  lines.push('D10*');
  for (const t of data.turns.filter(t => t.layer === layerIndex)) {
    lines.push(`X${gc(t.outerX)}Y${gc(-t.outerY)}D02*`);
    lines.push(`X${gc(t.innerX)}Y${gc(-t.innerY)}D01*`);
  }

  // 4. Spiral arcs on this layer (matched by numeric layer index).
  for (const arc of data.arcs.filter(a => a.layer === layerIndex)) {
    const I = gc(-arc.startX);
    const J = gc(arc.startY);
    const gCode = arc.sweepCW ? 'G02' : 'G03';
    lines.push('D10*');
    lines.push(`X${gc(arc.startX)}Y${gc(-arc.startY)}D02*`);
    lines.push(`${gCode}*`);
    lines.push(`X${gc(arc.endX)}Y${gc(-arc.endY)}I${I}J${J}D01*`);
    lines.push('G01*');
  }

  // 5. Selective pads on chaining vias (small via pad).
  lines.push('D11*');
  for (const cv of data.chainingVias) {
    if (cv.layers.includes(layerName)) {
      lines.push(`X${gc(cv.position.x)}Y${gc(-cv.position.y)}D03*`);
    }
  }

  // 6. Selective pads on bridge vias (small via pad).
  for (const bv of bus.bridgeVias) {
    if (bv.layers.includes(layerName)) {
      lines.push(`X${gc(bv.position.x)}Y${gc(-bv.position.y)}D03*`);
    }
  }

  // 6b. L3 exit vias are via-class internal junctions (Task 4b) — small via pad (D11).
  for (const ev of data.exitVias) {
    if (ev.layers.includes(layerName)) {
      lines.push(`X${gc(ev.position.x)}Y${gc(-ev.position.y)}D03*`);
    }
  }

  // 7. Selective pads on outer-bus / inner-join holes.
  //    'via' entries (k≥4 inner-join In1/In2 pads on exit-via holes) are via-class
  //    (D11) internal junctions — same treatment as chaining vias, no mask opening.
  lines.push('D11*');
  for (const bp of bus.extraPadTerminals.filter(bp => bp.layer === layerName && bp.padClass === 'via')) {
    lines.push(`X${gc(bp.x)}Y${gc(-bp.y)}D03*`);
  }
  //    'terminal' entries: the plated T1 entry hole is the F.Cu↔layer transition
  //    for the wye/phase-chain (B.Cu) and delta (one arc per layer → In1/In2/B.Cu)
  //    outer buses. Terminal-class (D12).
  lines.push('D12*');
  for (const bp of bus.extraPadTerminals.filter(bp => bp.layer === layerName && bp.padClass === 'terminal')) {
    lines.push(`X${gc(bp.x)}Y${gc(-bp.y)}D03*`);
  }

  // 8. Terminal-class pads: entry pads on F.Cu only. Stacking-tabs ear joint pads
  //    additionally get a B.Cu pad (the joint is soldered from either face), so
  //    each plated ear hole has copper on both outer layers. The B.Cu branch is
  //    empty for wye/delta/independent-adjacent (no ear terminals) → byte-stable.
  lines.push('D12*');
  if (layerName === 'F.Cu') {
    for (const term of data.terminals) {
      lines.push(`X${gc(term.x)}Y${gc(-term.y)}D03*`);
    }
  } else if (layerName === 'B.Cu') {
    for (const term of data.terminals) if (term.ear) {
      lines.push(`X${gc(term.x)}Y${gc(-term.y)}D03*`);
    }
  }

  lines.push('M02*');
  return lines.join('\n');
}

/** Soldermask Gerber — openings over via and terminal pads. */
export function renderSolderMask(
  layerName: string,
  data: GerberData,
  bus: BusRouting,
  maskExpansionMm: number,
  viaPadMm: number,
  termPadMm: number,
): string {
  const exp = maskExpansionMm;
  const lines = [
    // Mask gerbers draw the OPENINGS → negative-polarity file (KiCad convention).
    ...x2Attributes(`Soldermask,${layerName === 'F_Mask' ? 'Top' : 'Bot'}`, 'Negative'),
    `G04 PCB Stator - ${layerName} *`,
    '%FSLAX36Y36*%',
    '%MOMM*%',
    '%LPD*%',
    `%ADD10C,${(viaPadMm + exp * 2).toFixed(4)}*%`,
    `%ADD11C,${(termPadMm + exp * 2).toFixed(4)}*%`,
    'G75*',
  ];

  const cuLayer: CopperLayer = layerName === 'F_Mask' ? 'F.Cu' : 'B.Cu';

  // Open mask over via-class pads (chaining + bridge + exit vias) on this side.
  lines.push('D10*');
  for (const cv of data.chainingVias) {
    if (cv.layers.includes(cuLayer)) {
      lines.push(`X${gc(cv.position.x)}Y${gc(-cv.position.y)}D03*`);
    }
  }
  for (const bv of bus.bridgeVias) {
    if (bv.layers.includes(cuLayer)) {
      lines.push(`X${gc(bv.position.x)}Y${gc(-bv.position.y)}D03*`);
    }
  }
  // L3 exit vias are via-class internal junctions (Task 4b) → via-class opening.
  for (const ev of data.exitVias) {
    if (ev.layers.includes(cuLayer)) {
      lines.push(`X${gc(ev.position.x)}Y${gc(-ev.position.y)}D03*`);
    }
  }

  // Open mask over terminal-class pads on this side. Stacking-tabs ear joint pads
  // are opened on BOTH masks (soldered from either face); the B_Mask branch is
  // empty for wye/delta/independent-adjacent → byte-stable.
  lines.push('D11*');
  if (cuLayer === 'F.Cu') {
    for (const t of data.terminals) {
      lines.push(`X${gc(t.x)}Y${gc(-t.y)}D03*`);
    }
  } else if (cuLayer === 'B.Cu') {
    for (const t of data.terminals) if (t.ear) {
      lines.push(`X${gc(t.x)}Y${gc(-t.y)}D03*`);
    }
  }
  // Outer-bus entry holes get a selective terminal-class pad on their bus layer.
  // Only F.Cu/B.Cu terminal pads are on an exposed side and need a mask opening;
  // delta's In1/In2 pads and all k≥4 inner-join 'via' pads are internal junctions
  // (no mask). Filter to this side's copper layer and terminal class.
  for (const bp of bus.extraPadTerminals.filter(bp => bp.layer === cuLayer && bp.padClass === 'terminal')) {
    lines.push(`X${gc(bp.x)}Y${gc(-bp.y)}D03*`);
  }

  lines.push('M02*');
  return lines.join('\n');
}

/** Excellon drill file — T1 terminal-class holes, T2 small via holes. */
export function renderDrillFile(data: GerberData, bus: BusRouting, termHoleMm: number): string {
  // Stacking-tabs ear joint holes (1.0mm, tool T3) are a distinct group from the
  // entry-pad terminals (termHoleMm, T1). Both are 'terminal' rows in
  // `data.terminals`; the `ear` flag routes them apart. When there are no ears
  // (wye/delta/independent-adjacent) NOTHING below fires the T3 path, so the file
  // is byte-identical to before.
  const earHoles = data.terminals.filter(t => t.ear);
  const hasEars = earHoles.length > 0;
  const lines = [
    'M48',
    '; PCB Stator drill file (Phase 2: two tools)',
    'FMAT,2',
    'METRIC',
    `T1C${termHoleMm.toFixed(3)}`,
    'T2C0.400',
    ...(hasEars ? [`T3C${EAR_HOLE_DRILL_MM.toFixed(3)}`] : []),
    '%',
    'G90',
    'G05',
  ];

  // T1: terminal-class holes (entry pads only — Task 4b moved exit vias to T2;
  // stacking-tabs ear holes are excluded here and drilled on T3 below).
  lines.push('T1');
  const t1Holes: Array<{ x: number; y: number }> = [];
  for (const t of data.terminals) if (!t.ear) t1Holes.push({ x: t.x, y: t.y });
  for (const h of deduplicatePoints(t1Holes)) {
    lines.push(`X${h.x.toFixed(4)}Y${(-h.y).toFixed(4)}`);
  }

  // T2: small via holes (chaining + bridge + L3 exit vias). Tool size 0.4mm.
  lines.push('T2');
  const t2Holes: Array<{ x: number; y: number }> = [];
  for (const cv of data.chainingVias) t2Holes.push({ x: cv.position.x, y: cv.position.y });
  for (const bv of bus.bridgeVias) t2Holes.push({ x: bv.position.x, y: bv.position.y });
  for (const ev of data.exitVias) t2Holes.push({ x: ev.position.x, y: ev.position.y });
  for (const h of deduplicatePoints(t2Holes)) {
    lines.push(`X${h.x.toFixed(4)}Y${(-h.y).toFixed(4)}`);
  }

  // T3: stacking-tabs ear joint holes (1.0mm). Emitted only when ears exist.
  if (hasEars) {
    lines.push('T3');
    for (const h of deduplicatePoints(earHoles.map(t => ({ x: t.x, y: t.y })))) {
      lines.push(`X${h.x.toFixed(4)}Y${(-h.y).toFixed(4)}`);
    }
  }

  lines.push('M30');
  return lines.join('\n');
}

/** NPTH (non-plated) mounting holes as a separate Excellon file. */
export function renderMountingDrill(mountingHoleDia: number, holes: MountingHole[]): string {
  const lines = [
    'M48',
    '; PCB Stator NPTH mounting holes (non-plated, mechanical)',
    'FMAT,2',
    'METRIC',
    `T1C${mountingHoleDia.toFixed(3)}`,
    '%',
    'G90',
    'G05',
    'T1',
  ];
  for (const h of holes) {
    lines.push(`X${h.x.toFixed(4)}Y${(-h.y).toFixed(4)}`);
  }
  lines.push('M30');
  return lines.join('\n');
}

/** Board-edge Gerber — outer + inner (bore) concentric circles. */
export function renderEdgeCuts(outerRadius: number, innerRadius: number): string {
  const R = outerRadius;
  const r = innerRadius;
  const lines = [
    ...x2Attributes('Profile,NP'),
    'G04 PCB Stator - Edge_Cuts *',
    '%FSLAX36Y36*%',
    '%MOMM*%',
    '%LPD*%',
    '%ADD10C,0.0500*%',
    'G75*',
    'D10*',
  ];

  lines.push(`X${gc(R)}Y0D02*`);
  lines.push('G03*');
  lines.push(`X${gc(-R)}Y0I${gc(-R)}J0D01*`);
  lines.push(`X${gc(R)}Y0I${gc(R)}J0D01*`);

  lines.push(`X${gc(r)}Y0D02*`);
  lines.push(`X${gc(-r)}Y0I${gc(-r)}J0D01*`);
  lines.push(`X${gc(r)}Y0I${gc(r)}J0D01*`);

  lines.push('G01*');
  lines.push('M02*');
  return lines.join('\n');
}

function deduplicatePoints(pts: { x: number; y: number }[]): { x: number; y: number }[] {
  const seen = new Set<string>();
  return pts.filter(p => {
    const key = `${p.x.toFixed(3)},${p.y.toFixed(3)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ── Generic board-agnostic model (Tasks 6+: new board types) ───────────────

export interface BoardLayerSpec {
  name: string;
  fileFunction: string;
  polarity: 'Positive' | 'Negative';
  draws: GerberDraw[];
}

export interface GerberDraw {
  apertureDiaMm: number;
  kind: 'polyline' | 'flash' | 'region';
  points: Array<[number, number]>;
}

export interface DrillSpec {
  toolDiaMm: number;
  plated: boolean;
  holes: Array<[number, number]>;
}

export interface BoardDescriptor {
  boardName: string;                       // file prefix, e.g. 'encoder-ring'
  outline: Array<[number, number]>;        // closed board-edge polyline
  cutouts?: Array<Array<[number, number]>>; // additional closed Edge_Cuts loops (e.g. bore, internal windows)
  layers: BoardLayerSpec[];                // copper + mask + silk, in file order
  drills: DrillSpec[];
  x2CopperNames: string[];                 // full FileFunction strings, e.g. ['Copper,L1,Top','Copper,L2,Bot']
}

/** Assign each layer's draws a D-code, in first-appearance order starting at
 *  D10 — mirrors the fixed D10/D11/D12 convention used by the stator's
 *  hand-written emitters (trace / via / terminal apertures), generalized to
 *  however many distinct aperture sizes a board actually uses. */
function assignApertureCodes(draws: GerberDraw[]): Map<number, number> {
  const map = new Map<number, number>();
  let next = 10;
  for (const d of draws) {
    if (!map.has(d.apertureDiaMm)) map.set(d.apertureDiaMm, next++);
  }
  return map;
}

function renderGenericLayer(layer: BoardLayerSpec, boardName: string): string {
  const apByDia = assignApertureCodes(layer.draws);
  const lines: string[] = [
    ...x2Attributes(layer.fileFunction, layer.polarity),
    `G04 ${boardName} - ${layer.name} *`,
    '%FSLAX36Y36*%',
    '%MOMM*%',
    '%LPD*%',
  ];
  for (const [dia, code] of apByDia) {
    lines.push(`%ADD${code}C,${dia.toFixed(4)}*%`);
  }
  lines.push('G75*', 'G01*');

  let currentCode = -1;
  for (const d of layer.draws) {
    const code = apByDia.get(d.apertureDiaMm)!;
    if (code !== currentCode) {
      lines.push(`D${code}*`);
      currentCode = code;
    }
    if (d.kind === 'flash') {
      const [x, y] = d.points[0];
      lines.push(`X${gc(x)}Y${gc(-y)}D03*`);
    } else if (d.kind === 'polyline') {
      d.points.forEach(([x, y], i) => {
        lines.push(`X${gc(x)}Y${gc(-y)}${i === 0 ? 'D02' : 'D01'}*`);
      });
    } else {
      // region: filled polygon boundary
      lines.push('G36*');
      d.points.forEach(([x, y], i) => {
        lines.push(`X${gc(x)}Y${gc(-y)}${i === 0 ? 'D02' : 'D01'}*`);
      });
      lines.push('G37*');
    }
  }

  lines.push('M02*');
  return lines.join('\n');
}

export function renderGenericOutline(
  outline: Array<[number, number]>, boardName: string, cutouts: Array<Array<[number, number]>> = [],
): string {
  const lines = [
    ...x2Attributes('Profile,NP'),
    `G04 ${boardName} - Edge_Cuts *`,
    '%FSLAX36Y36*%', '%MOMM*%', '%LPD*%',
    '%ADD10C,0.0500*%', 'G75*', 'D10*',
  ];
  const loop = (pts: Array<[number, number]>) => pts.forEach(([x, y], i) => {
    lines.push(`X${gc(x)}Y${gc(-y)}${i === 0 ? 'D02' : 'D01'}*`);
  });
  loop(outline);
  for (const c of cutouts) loop(c);   // each closed inner loop (bore, window) on Edge_Cuts
  lines.push('G01*', 'M02*');
  return lines.join('\n');
}

function renderGenericDrillFile(spec: DrillSpec, toolNum: number): string {
  const lines = [
    'M48',
    `; ${spec.plated ? 'Plated' : 'Non-plated'} drill file`,
    'FMAT,2',
    'METRIC',
    `T${toolNum}C${spec.toolDiaMm.toFixed(3)}`,
    '%',
    'G90',
    'G05',
    `T${toolNum}`,
  ];
  for (const [x, y] of spec.holes) {
    lines.push(`X${x.toFixed(4)}Y${(-y).toFixed(4)}`);
  }
  lines.push('M30');
  return lines.join('\n');
}

/** Render a full board's Gerber/drill file set from a board-agnostic
 *  descriptor. Straight-line renderer only (`GerberDraw.points` carries no
 *  arc/curvature data) — intended for simpler future boards, not a
 *  byte-identical substitute for the stator's arc-heavy emitters above. */
export function emitBoard(desc: BoardDescriptor): Array<{ filename: string; content: string }> {
  const out: Array<{ filename: string; content: string }> = [];
  for (const layer of desc.layers) {
    out.push({ filename: `${desc.boardName}-${layer.name}.gbr`, content: renderGenericLayer(layer, desc.boardName) });
  }
  out.push({ filename: `${desc.boardName}-Edge_Cuts.gbr`, content: renderGenericOutline(desc.outline, desc.boardName, desc.cutouts) });

  const platedHoles = desc.drills.filter(d => d.plated);
  const nptHoles = desc.drills.filter(d => !d.plated);
  platedHoles.forEach((spec, i) => {
    out.push({ filename: `${desc.boardName}${i > 0 ? `-${i + 1}` : ''}.drl`, content: renderGenericDrillFile(spec, i + 1) });
  });
  nptHoles.forEach((spec, i) => {
    out.push({ filename: `${desc.boardName}-NPTH${i > 0 ? `-${i + 1}` : ''}.drl`, content: renderGenericDrillFile(spec, i + 1) });
  });

  return out;
}
